import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { OpenLocationCode } from "open-location-code";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";
import {
  ArrowLeft, MapPin, Upload, Camera, FileText, CheckCircle, Clock, XCircle,
  ShieldCheck, Storefront, Plus, DownloadSimple, X, Crosshair, Tag, CreditCard,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { usePaymentConfig, getPaymentOptionsForCurrency } from "@/hooks/usePaymentConfig";
import { useFxRates } from "@/hooks/useFxRates";
import PayoutSettings from "@/components/PayoutSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const olc = new OpenLocationCode();

const STAGE_LABEL = {
  pending_admin: "Waiting Admin Approval",
  admin_approved: "Admin Approved",
  pending_validator: "Waiting External Validator",
  validator_approved: "Validator Approved",
  listed: "Listed on Hub",
  sold: "Sold",
  rejected: "Rejected",
  legacy: "Legacy",
};

const STAGE_COLOR = {
  pending_admin: "bg-amber-100 text-amber-800 border-amber-300",
  admin_approved: "bg-sky-100 text-sky-800 border-sky-300",
  pending_validator: "bg-violet-100 text-violet-800 border-violet-300",
  validator_approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  listed: "bg-emerald-600 text-white border-emerald-700",
  sold: "bg-stone-200 text-stone-700 border-stone-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
  legacy: "bg-stone-100 text-stone-500 border-stone-200",
};

const STAGE_ICON = {
  pending_admin: Clock,
  admin_approved: ShieldCheck,
  pending_validator: Clock,
  validator_approved: CheckCircle,
  listed: Storefront,
  sold: CheckCircle,
  rejected: XCircle,
  legacy: Tag,
};

const TIMELINE_STEPS = [
  { key: "submitted", label: "Submitted by Seller" },
  { key: "admin_approved", label: "Admin Approved" },
  { key: "pending_validator", label: "External Validator Assigned" },
  { key: "validator_approved", label: "Validator Approved" },
  { key: "listed", label: "Listed on Hub" },
];

const STANDARD_LABEL = {
  verra_vcs: "Verra VCS",
  gold_standard: "Gold Standard",
  both: "Verra VCS + Gold Standard",
};

const LAND_TENURE_LABEL = {
  freehold: "Freehold",
  lease: "Lease",
  community: "Community",
  concession: "Concession",
};

const SDG_OPTIONS = [
  { code: "SDG1", label: "No Poverty" },
  { code: "SDG2", label: "Zero Hunger" },
  { code: "SDG3", label: "Good Health" },
  { code: "SDG5", label: "Gender Equality" },
  { code: "SDG6", label: "Clean Water" },
  { code: "SDG7", label: "Affordable Energy" },
  { code: "SDG8", label: "Decent Work" },
  { code: "SDG12", label: "Responsible Consumption" },
  { code: "SDG13", label: "Climate Action" },
  { code: "SDG14", label: "Life Below Water" },
  { code: "SDG15", label: "Life on Land" },
  { code: "SDG17", label: "Partnerships" },
];

const SDG_LABEL_BY_CODE = SDG_OPTIONS.reduce((acc, s) => {
  acc[s.code] = s.label;
  return acc;
}, {});

export default function CarbonHubPage() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showPayout, setShowPayout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [walletBalance, setWalletBalance] = useState(0);

  const fetchListings = useCallback(async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const res = await axios.get(`${API}/hub/listings`, { params });
      setListings(res.data?.listings || []);
      setCounts(res.data?.counts || {});
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchWallet = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/wallet/balance`, { withCredentials: true });
      setWalletBalance(r.data?.balance || 0);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { fetchListings(); }, [fetchListings]);
  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  const handleBuy = async () => {
    // Called by FlutterwaveBuyButton after successful verify
    setSelected(null);
    fetchListings();
    fetchWallet();
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="carbon-hub-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-1 text-stone-500 hover:text-stone-900 shrink-0" data-testid="hub-back-link">
            <ArrowLeft size={16} weight="bold" />
            <span className="text-sm font-body hidden sm:inline">Home</span>
          </Link>
          <div className="h-6 w-px bg-stone-200 hidden sm:block" />
          <h1 className="font-heading text-base sm:text-lg font-semibold text-stone-900">Carbon Hub</h1>
          <span className="text-xs text-stone-400 hidden lg:inline">Unified registry + marketplace</span>
          <div className="flex-1" />
          {user && (
            <span className="text-xs text-stone-500 hidden md:inline" data-testid="hub-wallet-balance">
              Wallet: <span className="text-emerald-700 font-semibold">${walletBalance.toFixed(2)}</span>
            </span>
          )}
          {user ? (
            <>
              <button
                onClick={() => setShowPayout(true)}
                className="text-stone-600 hover:text-emerald-700 text-xs font-medium hidden sm:inline-flex items-center gap-1 px-3 py-2 rounded-full border border-stone-200"
                data-testid="hub-payout-button"
              >
                Payout
              </button>
              <button
                onClick={() => setShowSubmit(true)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs sm:text-sm font-medium px-3 sm:px-4 py-1.5 sm:py-2 rounded-full flex items-center gap-1.5 transition-all shrink-0"
                data-testid="hub-submit-button"
              >
                <Plus size={14} weight="bold" /> <span className="hidden sm:inline">Submit Project</span><span className="sm:hidden">Submit</span>
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="hub-signin-cta" className="bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-full">
              Sign in to submit
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">
        <FilterBar counts={counts} filter={filter} setFilter={setFilter} />

        {loading ? (
          <div className="text-center py-20 text-stone-400">Loading...</div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20 text-stone-400" data-testid="hub-empty">
            No projects match this filter.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="hub-listings-grid">
            {listings.map((p) => (
              <ListingCard key={p.id} project={p} onClick={() => setSelected(p)} />
            ))}
          </div>
        )}
      </main>

      {selected && (
        <DetailDrawer
          project={selected}
          onClose={() => setSelected(null)}
          onBuy={handleBuy}
          currentUser={user}
        />
      )}

      {showSubmit && (
        <SubmitModal
          onClose={() => setShowSubmit(false)}
          onSubmitted={() => {
            setShowSubmit(false);
            fetchListings();
          }}
        />
      )}

      {showPayout && (
        <div className="fixed inset-0 z-[70] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="hub-payout-modal" onClick={() => setShowPayout(false)}>
          <div className="max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <PayoutSettings />
            <button onClick={() => setShowPayout(false)} className="mt-3 text-white text-xs hover:underline mx-auto block">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Filter bar ─────────────────────────────────────────────

function FilterBar({ counts, filter, setFilter }) {
  const filters = [
    { id: "all", label: "All" },
    { id: "pending_admin", label: "Awaiting Admin" },
    { id: "pending_validator", label: "Awaiting Validator" },
    { id: "listed", label: "Available to Buy" },
    { id: "sold", label: "Sold" },
    { id: "rejected", label: "Rejected" },
  ];
  return (
    <div className="flex sm:flex-wrap items-center gap-2 mb-6 sm:mb-8 overflow-x-auto sm:overflow-visible pb-2 sm:pb-0 -mx-3 sm:mx-0 px-3 sm:px-0 scrollbar-thin" data-testid="hub-filter-bar">
      {filters.map((f) => {
        const active = filter === f.id;
        const count = f.id === "all"
          ? Object.values(counts).reduce((a, b) => a + b, 0)
          : counts[f.id] || 0;
        return (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all font-body whitespace-nowrap shrink-0 ${
              active
                ? "bg-stone-900 text-white border-stone-900"
                : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
            }`}
            data-testid={`hub-filter-${f.id}`}
          >
            {f.label} <span className={active ? "text-stone-300" : "text-stone-400"}>({count})</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Card ───────────────────────────────────────────────────

function ListingCard({ project, onClick }) {
  const Icon = STAGE_ICON[project.status] || Clock;
  return (
    <button
      onClick={onClick}
      className="text-left bg-white border border-stone-200 rounded-3xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
      data-testid={`hub-card-${project.id}`}
    >
      <div className="h-32 bg-gradient-to-br from-emerald-100 via-emerald-50 to-sky-50 relative flex items-center justify-center">
        {project.proof_photo_path ? (
          <img
            src={`${API}/hub/listings/${project.id}/file/proof_photo`}
            alt="proof"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <MapPin size={36} weight="duotone" className="text-emerald-700/40" />
        )}
        <span
          className={`absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${STAGE_COLOR[project.status]}`}
        >
          <Icon size={10} weight="bold" /> {STAGE_LABEL[project.status]}
        </span>
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading text-base font-semibold text-stone-900 line-clamp-1">
            {project.location_name || `${project.project_type} project`}
          </h3>
        </div>
        <p className="text-xs text-stone-500 mb-3 line-clamp-2 font-body">
          {project.description || "No description provided"}
        </p>
        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-stone-100">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-stone-400">Area</p>
            <p className="text-sm font-semibold text-stone-900">{project.area_ha} ha</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-stone-400">tCO₂e</p>
            <p className="text-sm font-semibold text-emerald-700">{project.estimated_credits || 0}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-stone-400">Price</p>
            <p className="text-sm font-semibold text-stone-900">${project.total_price || 0}</p>
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Detail Drawer ──────────────────────────────────────────

function DetailDrawer({ project, onClose, onBuy, currentUser }) {
  const history = project.approval_history || [];
  const completedStages = new Set(history.map((h) => h.stage));
  if (project.status === "listed") completedStages.add("validator_approved");

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="hub-detail-drawer">
      <button className="flex-1 bg-stone-900/40 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
      <div className="w-full max-w-2xl bg-white overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-stone-200 p-5 flex items-center gap-3">
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="hub-detail-close">
            <X size={18} weight="bold" />
          </button>
          <h2 className="font-heading text-lg font-semibold text-stone-900 flex-1">
            {project.location_name || project.project_type}
          </h2>
          <span
            className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${STAGE_COLOR[project.status]}`}
          >
            {STAGE_LABEL[project.status]}
          </span>
        </header>

        <div className="p-6 space-y-6">
          {/* Hero photo */}
          {project.proof_photo_path && (
            <div className="rounded-2xl overflow-hidden aspect-video bg-stone-100">
              <img
                src={`${API}/hub/listings/${project.id}/file/proof_photo`}
                alt="Project proof"
                className="w-full h-full object-cover"
                data-testid="hub-detail-photo"
              />
            </div>
          )}

          {/* Key facts */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Fact label="Seller" value={project.seller_name} />
            <Fact label="Project Type" value={project.project_type} />
            <Fact label="Area" value={`${project.area_ha} ha`} />
            <Fact label="Duration" value={`${project.project_duration || 30} years`} />
            <Fact label="Credits" value={`${project.estimated_credits || 0} tCO₂e`} highlight />
            <Fact label="Price / ton" value={`$${project.price_per_ton || 0}`} />
            <Fact label="Total Price" value={`$${project.total_price || 0}`} highlight />
            <Fact label="Location" value={`${project.lat?.toFixed(3)}, ${project.lon?.toFixed(3)}`} />
          </div>

          {project.description && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-1">Description</p>
              <p className="text-sm text-stone-600 leading-relaxed">{project.description}</p>
            </div>
          )}

          <StandardsCompliance project={project} />

          {/* Approval timeline */}
          <div>
            <h3 className="font-heading text-sm font-semibold text-stone-900 mb-3">Approval Flow</h3>
            <ol className="space-y-3" data-testid="hub-detail-timeline">
              {TIMELINE_STEPS.map((step, idx) => {
                const event = history.find((h) => h.stage === step.key);
                const done = !!event || (project.status === "listed" && idx < TIMELINE_STEPS.length);
                const isReject = project.status === "rejected" && !event;
                const Icon = done ? CheckCircle : Clock;
                return (
                  <li key={step.key} className="flex items-start gap-3" data-testid={`timeline-step-${step.key}`}>
                    <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      done ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-400"
                    }`}>
                      <Icon size={14} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${done ? "text-stone-900" : "text-stone-400"}`}>
                        {step.label}
                      </p>
                      {event && (
                        <p className="text-xs text-stone-500 mt-0.5">
                          by {event.by_name} · {new Date(event.at).toLocaleString()}
                          {event.notes && <span className="block italic">{event.notes}</span>}
                        </p>
                      )}
                      {!event && !done && !isReject && (
                        <p className="text-xs text-stone-400">Pending</p>
                      )}
                    </div>
                  </li>
                );
              })}
              {project.status === "rejected" && (
                <li className="flex items-start gap-3" data-testid="timeline-rejected">
                  <div className="mt-0.5 w-7 h-7 rounded-full bg-red-100 text-red-700 flex items-center justify-center">
                    <XCircle size={14} weight="bold" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-700">Rejected</p>
                    {project.rejection_reason && (
                      <p className="text-xs text-stone-500 italic">{project.rejection_reason}</p>
                    )}
                  </div>
                </li>
              )}
            </ol>
          </div>

          {/* Documents */}
          <div>
            <h3 className="font-heading text-sm font-semibold text-stone-900 mb-3">Documents</h3>
            <div className="space-y-2">
              {project.council_letter_path && (
                <DocLink id={project.id} kind="council_letter" label="Local Council Letter" testId="doc-council" />
              )}
              {project.proof_photo_path && (
                <DocLink id={project.id} kind="proof_photo" label="Geotagged Proof Photo" testId="doc-photo" />
              )}
              {project.validator_report_path && (
                <DocLink id={project.id} kind="validator_report" label={`Validator Report — ${project.validator_name || ""}`} testId="doc-validator" />
              )}
              {!project.council_letter_path && !project.validator_report_path && (
                <p className="text-xs text-stone-400 italic">No documents uploaded yet.</p>
              )}
            </div>
          </div>

          {/* Buy action */}
          {project.status === "listed" && currentUser && currentUser._id !== project.seller_id && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm font-medium text-stone-900">Ready to purchase</p>
                <p className="text-xs text-stone-500">{project.estimated_credits} tCO₂e for ${project.total_price}</p>
              </div>
              <FlutterwaveBuyButton project={project} onSuccess={() => onBuy(project.id)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StandardsCompliance({ project }) {
  const sdgs = Array.isArray(project.sdg_contributions) ? project.sdg_contributions : [];
  const has = project.standard || project.methodology_id || project.land_tenure_type
    || project.uncertainty_percent || project.sampling_plot_count || sdgs.length > 0;
  if (!has) return null;
  return (
    <div data-testid="hub-detail-standards">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} weight="duotone" className="text-emerald-700" />
        <h3 className="font-heading text-sm font-semibold text-stone-900">Standards &amp; Compliance</h3>
        {project.standard && (
          <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full">
            {STANDARD_LABEL[project.standard] || project.standard}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {project.methodology_id && (
          <Fact label="Methodology" value={project.methodology_id} />
        )}
        {project.land_tenure_type && (
          <Fact label="Land tenure" value={LAND_TENURE_LABEL[project.land_tenure_type] || project.land_tenure_type} />
        )}
        {project.uncertainty_percent > 0 && (
          <Fact label="Uncertainty (95% CI)" value={`${project.uncertainty_percent}%`} />
        )}
        {project.sampling_plot_count > 0 && (
          <Fact
            label="Sampling design"
            value={`${project.sampling_plot_count} plots${project.sampling_plot_size_m2 ? ` × ${project.sampling_plot_size_m2} m²` : ""}`}
          />
        )}
      </div>
      {sdgs.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2">
            SDG contributions <span className="text-stone-400 normal-case tracking-normal">(Gold Standard co-benefits)</span>
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="hub-detail-sdgs">
            {sdgs.map((code) => (
              <span key={code}
                className="text-[11px] px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800"
                data-testid={`hub-detail-sdg-${code}`}>
                {code}{SDG_LABEL_BY_CODE[code] ? ` · ${SDG_LABEL_BY_CODE[code]}` : ""}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SequestrationCard({ detection, detecting, error, hasCoords }) {
  if (!hasCoords) {
    return (
      <div
        data-testid="hub-submit-detection-card"
        className="bg-stone-50 border border-stone-200 border-dashed rounded-2xl p-4 text-center"
      >
        <p className="text-xs text-stone-500">
          Enter latitude &amp; longitude above — the AI will automatically detect the carbon-sequestration potential from real satellite data.
        </p>
      </div>
    );
  }
  if (detecting) {
    return (
      <div
        data-testid="hub-submit-detection-card"
        className="bg-emerald-50/60 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3"
      >
        <div className="w-4 h-4 rounded-full border-2 border-emerald-600 border-t-transparent animate-spin" />
        <p className="text-xs text-emerald-800 font-medium">Detecting carbon sequestration from MODIS satellite &amp; NASA POWER…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div
        data-testid="hub-submit-detection-card"
        className="bg-amber-50 border border-amber-200 rounded-2xl p-4"
      >
        <p className="text-xs text-amber-800">⚠ {error}</p>
      </div>
    );
  }
  if (!detection) return null;

  const det = detection.detection || {};
  const isReal = det.ndvi_source && det.ndvi_source !== "modeled";

  return (
    <div
      data-testid="hub-submit-detection-card"
      className="bg-gradient-to-br from-emerald-50 to-teal-50/40 border border-emerald-200 rounded-2xl p-5 space-y-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] font-heading font-semibold text-emerald-700">
            AI Carbon-Sequestration Detection
          </p>
          <p className="text-[11px] text-stone-500 mt-0.5">
            Auto-computed from real satellite data — updates as you change coordinates, area, project type, and duration.
          </p>
        </div>
        {isReal && (
          <span className="text-[10px] uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-full font-heading font-semibold">
            Real satellite data
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <MetricTile
          label="Annual sequestration"
          value={`${detection.annual_sequestration_tco2e?.toLocaleString()} tCO₂e/yr`}
          highlight
          testid="hub-submit-annual"
        />
        <MetricTile
          label={`Lifetime (${detection.duration_years} yr)`}
          value={`${detection.lifetime_sequestration_tco2e?.toLocaleString()} tCO₂e`}
          highlight
          testid="hub-submit-lifetime"
        />
        <MetricTile
          label="Est. market value"
          value={`$${detection.estimated_market_value_usd?.toLocaleString()}`}
          testid="hub-submit-value"
        />
        <MetricTile
          label="Per hectare / year"
          value={`${detection.per_hectare_annual_tco2e} tCO₂e/ha·yr`}
          testid="hub-submit-perha"
        />
        <MetricTile
          label="NDVI (vegetation vigour)"
          value={`${det.ndvi?.toFixed(3)} — ${det.vigor_class || ""}`}
          testid="hub-submit-ndvi"
        />
        <MetricTile
          label="Uncertainty (95% CI)"
          value={`± ${detection.uncertainty_pct_95ci}%`}
          testid="hub-submit-uncertainty-detected"
        />
      </div>

      <div className="text-[11px] text-stone-500 border-t border-emerald-200/70 pt-3 leading-relaxed">
        <p>
          <span className="font-heading font-semibold text-stone-700">Data lineage:</span> NDVI from{" "}
          <span className="font-mono">{det.ndvi_source || "unavailable"}</span>
          {det.ndvi_observation_date ? ` (obs. ${det.ndvi_observation_date})` : ""} · climate from{" "}
          <span className="font-mono">{det.climate_source || "n/a"}</span>
          {det.avg_temp_c_30d != null ? `, ${det.avg_temp_c_30d}°C avg` : ""}
          {det.total_precip_mm_30d != null ? `, ${det.total_precip_mm_30d}mm precip (30d)` : ""}.
        </p>
        <p className="mt-1"><span className="font-heading font-semibold text-stone-700">Methodology:</span> {det.yield_methodology_note}</p>
      </div>
    </div>
  );
}

function MetricTile({ label, value, highlight = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl p-3 ${
        highlight ? "bg-emerald-600 text-white" : "bg-white border border-emerald-100"
      }`}
    >
      <p className={`text-[10px] uppercase tracking-wider ${highlight ? "text-emerald-100" : "text-stone-500"}`}>
        {label}
      </p>
      <p className={`text-sm font-heading font-semibold ${highlight ? "text-white" : "text-stone-900"} mt-0.5`}>
        {value}
      </p>
    </div>
  );
}

function Fact({ label, value, highlight = false }) {
  return (
    <div className={`rounded-xl p-3 ${highlight ? "bg-emerald-50 border border-emerald-100" : "bg-stone-50"}`}>
      <p className="text-[10px] uppercase tracking-wider text-stone-400">{label}</p>
      <p className={`text-sm font-semibold ${highlight ? "text-emerald-800" : "text-stone-900"}`}>{value}</p>
    </div>
  );
}

function DocLink({ id, kind, label, testId }) {
  return (
    <a
      href={`${API}/hub/listings/${id}/file/${kind}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-4 py-3 bg-stone-50 hover:bg-stone-100 border border-stone-200 rounded-xl transition-colors"
      data-testid={testId}
    >
      <FileText size={16} className="text-stone-500" />
      <span className="text-sm text-stone-700 flex-1">{label}</span>
      <DownloadSimple size={14} className="text-stone-400" />
    </a>
  );
}

// ─── Submit Modal ───────────────────────────────────────────

function LocationPicker({ lat, lon, setLatLon }) {
  function ClickHandler() {
    useMapEvents({
      click(e) {
        setLatLon(e.latlng.lat, e.latlng.lng);
      },
    });
    return null;
  }
  const center = lat && lon ? [lat, lon] : [20, 0];
  const zoom = lat && lon ? 8 : 2;
  return (
    <div className="h-64 rounded-2xl overflow-hidden border border-stone-200" data-testid="hub-submit-map">
      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} key={`${lat}-${lon}`}>
        <TileLayer attribution="&copy; OSM" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <ClickHandler />
        {lat && lon && <Marker position={[lat, lon]} />}
      </MapContainer>
    </div>
  );
}

function SubmitModal({ onClose, onSubmitted }) {
  const [step, setStep] = useState(1);
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [locationName, setLocationName] = useState("");
  const [projectType, setProjectType] = useState("reforestation");
  const [speciesType, setSpeciesType] = useState("mixed");
  const [areaHa, setAreaHa] = useState(10);
  const [duration, setDuration] = useState(30);
  const [pricePerTon, setPricePerTon] = useState(15);
  const [description, setDescription] = useState("");
  const [councilFile, setCouncilFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoGeotag, setPhotoGeotag] = useState(null);
  // Optional Verra VCS / Gold Standard compliance fields
  const [standard, setStandard] = useState("");
  const [methodologyId, setMethodologyId] = useState("");
  const [landTenureType, setLandTenureType] = useState("");
  const [samplingPlotCount, setSamplingPlotCount] = useState(0);
  const [samplingPlotSize, setSamplingPlotSize] = useState(0);
  const [uncertaintyPct, setUncertaintyPct] = useState(0);
  const [sdgContributions, setSdgContributions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // AI-assisted carbon-sequestration detection state
  const [detection, setDetection] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState("");
  const photoRef = useRef(null);
  const latInputRef = useRef(null);
  const manualCoordsRef = useRef(null);
  const [manualHighlight, setManualHighlight] = useState(false);
  const [pasteInput, setPasteInput] = useState("");
  const [pasteError, setPasteError] = useState("");
  const [pasteOk, setPasteOk] = useState(false);

  const focusManualCoords = () => {
    manualCoordsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setManualHighlight(true);
    setTimeout(() => {
      latInputRef.current?.focus();
      latInputRef.current?.select?.();
    }, 350);
    setTimeout(() => setManualHighlight(false), 1800);
  };

  // Parse a pasted coordinate string. Accepts:
  //   "-1.286389, 36.817223"         (decimal)
  //   "-1.286389 36.817223"          (decimal, whitespace)
  //   "1.286389S, 36.817223E"        (decimal with hemisphere)
  //   "1°17'11\"S 36°49'02\"E"       (DMS)
  //   "1°17'11.5\" S, 36°49'2\" E"   (DMS with decimals & spaces)
  //   "1 17 11 S 36 49 02 E"         (bare DMS)
  //   "6GCRPR6C+24"                  (Open Location Code / Plus Code, full)
  //   "PR6C+24 Nairobi"              (short Plus Code with locality — locality ignored)
  // Returns {lat, lon} on success, null otherwise.
  const parseCoordString = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();

    // 0) Open Location Code / Plus Code — accept full codes only (short codes
    // need a reference lat/lon which we don't have at paste time).
    const plusMatch = s.match(/([23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,7})/i);
    if (plusMatch) {
      const code = plusMatch[1].toUpperCase();
      try {
        if (olc.isFull(code)) {
          const area = olc.decode(code);
          return { lat: area.latitudeCenter, lon: area.longitudeCenter };
        }
      } catch (_) { /* fall through */ }
    }

    // 1) Plain decimal: "lat, lon" or "lat lon" with optional trailing hemisphere letters.
    const decRe = /^\s*(-?\d+(?:\.\d+)?)\s*([NSns])?\s*[,;\s]\s*(-?\d+(?:\.\d+)?)\s*([EWew])?\s*$/;
    const dm = s.match(decRe);
    if (dm) {
      let la = parseFloat(dm[1]);
      let lo = parseFloat(dm[3]);
      if (dm[2] && dm[2].toUpperCase() === "S") la = -Math.abs(la);
      if (dm[2] && dm[2].toUpperCase() === "N") la = Math.abs(la);
      if (dm[4] && dm[4].toUpperCase() === "W") lo = -Math.abs(lo);
      if (dm[4] && dm[4].toUpperCase() === "E") lo = Math.abs(lo);
      if (Math.abs(la) <= 90 && Math.abs(lo) <= 180) return { lat: la, lon: lo };
    }

    // 2) DMS: capture two blocks of (deg, min, sec, hemisphere).
    // Accept °, º, d, or spaces; ', ′; ", ″; separators comma/semicolon/whitespace.
    const dmsBlock = String.raw`(\d+(?:\.\d+)?)\s*(?:°|º|d|\s)\s*(\d+(?:\.\d+)?)?\s*(?:['′]|\s)?\s*(\d+(?:\.\d+)?)?\s*(?:["″])?\s*([NSEWnsew])`;
    const dmsRe = new RegExp(`^\\s*${dmsBlock}\\s*[,;\\s]+\\s*${dmsBlock}\\s*$`);
    const m2 = s.match(dmsRe);
    if (m2) {
      const toDec = (deg, min, sec, hemi) => {
        const d = parseFloat(deg) || 0;
        const mi = parseFloat(min) || 0;
        const se = parseFloat(sec) || 0;
        let v = d + mi / 60 + se / 3600;
        const h = hemi.toUpperCase();
        if (h === "S" || h === "W") v = -v;
        return v;
      };
      const a = toDec(m2[1], m2[2], m2[3], m2[4]);
      const b = toDec(m2[5], m2[6], m2[7], m2[8]);
      // Figure out which is lat vs lon from the hemisphere letters.
      const aIsLat = ["N", "S"].includes(m2[4].toUpperCase());
      const la = aIsLat ? a : b;
      const lo = aIsLat ? b : a;
      if (Math.abs(la) <= 90 && Math.abs(lo) <= 180) return { lat: la, lon: lo };
    }
    return null;
  };

  const applyPastedCoords = (value) => {
    setPasteInput(value);
    if (!value.trim()) { setPasteError(""); setPasteOk(false); return; }
    const parsed = parseCoordString(value);
    if (parsed) {
      setLat(Number(parsed.lat.toFixed(6)));
      setLon(Number(parsed.lon.toFixed(6)));
      setPasteError("");
      setPasteOk(true);
    } else {
      setPasteOk(false);
      setPasteError('Unrecognised format — try "-1.286389, 36.817223", "1°17\'11"S 36°49\'02"E", or a Plus Code "6GCRPR6C+24"');
    }
  };

  // Reverse-geocode preview (city, country) — debounced 500ms, only when both coords set.
  const [placeName, setPlaceName] = useState("");
  const [placeLoading, setPlaceLoading] = useState(false);
  useEffect(() => {
    if (lat == null || lon == null) { setPlaceName(""); return; }
    const t = setTimeout(async () => {
      setPlaceLoading(true);
      try {
        const r = await axios.get(`${API}/geocode/reverse`, {
          params: { lat, lon },
          timeout: 8000,
        });
        setPlaceName(r.data?.short_name || "");
      } catch { setPlaceName(""); }
      finally { setPlaceLoading(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [lat, lon]);

  // Auto-detect carbon sequestration whenever the location / area / project-type / duration change.
  // Debounced 800 ms so typing doesn't hammer the API.
  useEffect(() => {
    if (lat == null || lon == null || !areaHa || areaHa <= 0) {
      setDetection(null);
      return;
    }
    const t = setTimeout(async () => {
      setDetecting(true);
      setDetectError("");
      try {
        const r = await axios.get(`${API}/carbon/sequestration-detect`, {
          params: {
            lat, lon,
            area_ha: areaHa,
            project_type: projectType,
            duration_years: duration,
          },
          timeout: 25000,
        });
        setDetection(r.data);
      } catch (e) {
        setDetectError(e.response?.data?.detail || "Auto-detection unavailable — you can still submit and the validator will estimate.");
        setDetection(null);
      } finally {
        setDetecting(false);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [lat, lon, areaHa, projectType, duration]);

  const autoLocate = () => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported by this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude);
        setLon(pos.coords.longitude);
      },
      (err) => setError(`Location error: ${err.message}`),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const onPhotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    // Capture geotag at the moment of upload
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setPhotoGeotag({
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            captured_at: new Date().toISOString(),
          });
        },
        () => {
          // Fallback: use project lat/lon
          setPhotoGeotag({ lat, lon, captured_at: new Date().toISOString() });
        },
        { enableHighAccuracy: true, timeout: 8000 },
      );
    } else {
      setPhotoGeotag({ lat, lon, captured_at: new Date().toISOString() });
    }
  };

  const submit = async () => {
    setError("");
    if (!lat || !lon) return setError("Please set the project location on the map.");
    if (!councilFile) return setError("Please upload the local council letter.");
    if (!photoFile) return setError("Please take/upload a geotagged photo.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("lat", String(lat));
      fd.append("lon", String(lon));
      fd.append("area_ha", String(areaHa));
      fd.append("project_type", projectType);
      fd.append("species_type", speciesType);
      fd.append("project_duration", String(duration));
      fd.append("price_per_ton", String(pricePerTon));
      fd.append("description", description);
      fd.append("location_name", locationName);
      if (photoGeotag) {
        fd.append("photo_lat", String(photoGeotag.lat));
        fd.append("photo_lon", String(photoGeotag.lon));
        fd.append("captured_at", photoGeotag.captured_at);
      }
      fd.append("council_letter", councilFile);
      fd.append("proof_photo", photoFile);
      // Optional Verra VCS / Gold Standard compliance fields
      if (standard) fd.append("standard", standard);
      if (methodologyId) fd.append("methodology_id", methodologyId);
      if (landTenureType) fd.append("land_tenure_type", landTenureType);
      if (samplingPlotCount) fd.append("sampling_plot_count", String(samplingPlotCount));
      if (samplingPlotSize) fd.append("sampling_plot_size_m2", String(samplingPlotSize));
      if (uncertaintyPct) fd.append("uncertainty_percent", String(uncertaintyPct));
      if (sdgContributions.length > 0) fd.append("sdg_contributions", JSON.stringify(sdgContributions));
      await axios.post(`${API}/hub/submit`, fd, {
        withCredentials: true,
      });
      onSubmitted();
    } catch (e) {
      setError(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="hub-submit-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center gap-3">
          <h2 className="font-heading text-lg font-semibold text-stone-900 flex-1">Submit Carbon Project</h2>
          <span className="text-xs text-stone-400">Step {step} of 4</span>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="hub-submit-close">
            <X size={18} weight="bold" />
          </button>
        </header>

        <div className="p-6 space-y-5">
          {step === 1 && (
            <>
              <SectionTitle title="1. Project Location" hint="Use your current location, tap the map, or enter coordinates manually below." />
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={autoLocate}
                  className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 px-3 py-2 rounded-full flex items-center gap-1.5"
                  data-testid="hub-autolocate-btn"
                >
                  <Crosshair size={12} weight="bold" /> Use my current location
                </button>
                <button
                  onClick={focusManualCoords}
                  className="text-xs bg-stone-50 text-stone-700 border border-stone-200 hover:bg-stone-100 px-3 py-2 rounded-full flex items-center gap-1.5"
                  data-testid="hub-manual-coords-btn"
                >
                  <Plus size={12} weight="bold" /> Enter coordinates manually
                </button>
              </div>
              <LocationPicker lat={lat} lon={lon} setLatLon={(la, lo) => { setLat(la); setLon(lo); }} />
              <div
                ref={manualCoordsRef}
                className={`space-y-3 rounded-xl p-3 -m-3 transition-all duration-500 ${
                  manualHighlight
                    ? "bg-emerald-50 ring-2 ring-emerald-300"
                    : "bg-transparent ring-0"
                }`}
                data-testid="hub-manual-coords-section"
              >
                <FormField label="Paste coordinates (decimal, DMS, or Plus Code)">
                  <div className="relative">
                    <input
                      type="text"
                      value={pasteInput}
                      onChange={(e) => applyPastedCoords(e.target.value)}
                      onPaste={(e) => {
                        const text = e.clipboardData?.getData("text");
                        if (text) {
                          e.preventDefault();
                          applyPastedCoords(text);
                        }
                      }}
                      placeholder={`-1.286389, 36.817223   |   1°17'11"S 36°49'02"E   |   6GCRPR6C+24`}
                      className={`w-full border rounded-lg px-3 py-2 pr-16 text-sm font-mono focus:outline-none transition-colors ${
                        pasteError
                          ? "border-red-300 focus:border-red-500"
                          : pasteOk
                          ? "border-emerald-400 focus:border-emerald-500"
                          : "border-stone-200 focus:border-emerald-500"
                      }`}
                      data-testid="hub-submit-paste-coords"
                    />
                    {pasteOk && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-emerald-600" data-testid="hub-paste-parsed-ok">
                        parsed ✓
                      </span>
                    )}
                  </div>
                  {pasteError && (
                    <p className="text-[11px] text-red-600 mt-1" data-testid="hub-paste-error">{pasteError}</p>
                  )}
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                <FormField label="Latitude (decimal degrees, ≥6 decimals for Verra/GS)">
                  <input
                    ref={latInputRef}
                    type="number"
                    inputMode="decimal"
                    step="0.000001"
                    min="-90"
                    max="90"
                    value={lat ?? ""}
                    placeholder="e.g. -1.286389"
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "") { setLat(null); return; }
                      const n = parseFloat(v);
                      if (Number.isFinite(n) && n >= -90 && n <= 90) setLat(n);
                    }}
                    onBlur={(e) => {
                      const n = parseFloat(e.target.value);
                      if (!Number.isFinite(n) || n < -90 || n > 90) {
                        e.target.value = lat != null ? String(lat) : "";
                      }
                    }}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-emerald-500"
                    data-testid="hub-submit-lat-input"
                  />
                </FormField>
                <FormField label="Longitude (decimal degrees, ≥6 decimals for Verra/GS)">
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.000001"
                    min="-180"
                    max="180"
                    value={lon ?? ""}
                    placeholder="e.g. 36.817223"
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "") { setLon(null); return; }
                      const n = parseFloat(v);
                      if (Number.isFinite(n) && n >= -180 && n <= 180) setLon(n);
                    }}
                    onBlur={(e) => {
                      const n = parseFloat(e.target.value);
                      if (!Number.isFinite(n) || n < -180 || n > 180) {
                        e.target.value = lon != null ? String(lon) : "";
                      }
                    }}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-emerald-500"
                    data-testid="hub-submit-lon-input"
                  />
                </FormField>
                </div>
                {(placeName || placeLoading) && (
                  <div
                    className="flex items-start gap-2 bg-emerald-50/70 border border-emerald-100 rounded-lg px-3 py-2 text-xs text-emerald-800"
                    data-testid="hub-place-preview"
                  >
                    <MapPin size={14} weight="fill" className="mt-0.5 flex-shrink-0 text-emerald-600" />
                    <span className="flex-1">
                      {placeLoading ? (
                        <span className="text-emerald-700/70">Resolving place name…</span>
                      ) : (
                        <>
                          <span className="font-semibold">Detected: </span>
                          <span data-testid="hub-place-name">{placeName}</span>
                        </>
                      )}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-stone-500 -mt-1">
                Paste coordinates in decimal (e.g. <span className="font-mono">-1.286389, 36.817223</span>), DMS (e.g. <span className="font-mono">1°17'11"S 36°49'02"E</span>), or a Plus Code (e.g. <span className="font-mono">6GCRPR6C+24</span>). You can also tap the map, use your current location, or type into the boxes above. Verra VCS &amp; Gold Standard both require at least 6 decimal places (≈ 0.1 m precision).
              </p>
              <FormField label="Location name (e.g. 'North Ridge, Kenya')">
                <input
                  type="text"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                  data-testid="hub-submit-location-name"
                />
              </FormField>
            </>
          )}

          {step === 2 && (
            <>
              <SectionTitle title="2. Project Details" hint="These figures drive the carbon calculation." />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormField label="Project type">
                  <select value={projectType} onChange={(e) => setProjectType(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                    data-testid="hub-submit-type">
                    <option value="reforestation">Reforestation (IPCC AR)</option>
                    <option value="afforestation">Afforestation (IPCC AR)</option>
                    <option value="conservation">Conservation / REDD+ (Verra VM0007)</option>
                    <option value="mangrove">Mangrove restoration (Verra VM0033 blue carbon)</option>
                    <option value="agroforestry">Agroforestry (Gold Standard AR)</option>
                    <option value="peatland">Peatland rewetting (IPCC Wetlands)</option>
                    <option value="grassland">Grassland restoration</option>
                  </select>
                </FormField>
                <FormField label="Species">
                  <select value={speciesType} onChange={(e) => setSpeciesType(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                    data-testid="hub-submit-species">
                    <option value="mixed">Mixed native</option>
                    <option value="hardwood">Hardwood</option>
                    <option value="softwood">Softwood</option>
                    <option value="mangrove">Mangrove</option>
                  </select>
                </FormField>
                <FormField label="Area (hectares)">
                  <input type="number" min="0.1" step="0.1" value={areaHa} onChange={(e) => setAreaHa(parseFloat(e.target.value) || 0)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                    data-testid="hub-submit-area" />
                </FormField>
                <FormField label="Project duration (years)">
                  <input type="number" min="1" max="100" value={duration} onChange={(e) => setDuration(parseInt(e.target.value) || 30)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                    data-testid="hub-submit-duration" />
                </FormField>
                <FormField label="Price per tCO₂e ($)">
                  <input type="number" min="1" step="0.5" value={pricePerTon} onChange={(e) => setPricePerTon(parseFloat(e.target.value) || 15)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                    data-testid="hub-submit-price" />
                </FormField>
              </div>
              <FormField label="Description (visible to buyers)">
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                  data-testid="hub-submit-description" />
              </FormField>

              <SequestrationCard
                detection={detection}
                detecting={detecting}
                error={detectError}
                hasCoords={lat != null && lon != null}
              />

              <details className="bg-emerald-50/60 border border-emerald-100 rounded-xl px-4 py-3" data-testid="hub-submit-standards-toggle">
                <summary className="text-sm font-medium text-emerald-900 cursor-pointer select-none">
                  Standards &amp; Compliance <span className="text-xs text-emerald-700/70 font-normal">(optional — Verra VCS / Gold Standard)</span>
                </summary>
                <p className="text-[11px] text-stone-500 mt-2 mb-3">
                  Completing these fields lets buyers see your project&apos;s methodology, land tenure, sampling design, uncertainty band, and SDG contributions on the public listing.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormField label="Certification standard">
                    <select value={standard} onChange={(e) => setStandard(e.target.value)}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                      data-testid="hub-submit-standard">
                      <option value="">— not selected —</option>
                      <option value="verra_vcs">Verra VCS</option>
                      <option value="gold_standard">Gold Standard</option>
                      <option value="both">Verra VCS + Gold Standard</option>
                    </select>
                  </FormField>
                  <FormField label="Methodology ID (e.g. VM0007, AR-ACM0003, GS AR-Reforestation v2.0)">
                    <input type="text" value={methodologyId} onChange={(e) => setMethodologyId(e.target.value)}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                      data-testid="hub-submit-methodology" />
                  </FormField>
                  <FormField label="Land tenure">
                    <select value={landTenureType} onChange={(e) => setLandTenureType(e.target.value)}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                      data-testid="hub-submit-land-tenure">
                      <option value="">— not selected —</option>
                      <option value="freehold">Freehold</option>
                      <option value="lease">Lease</option>
                      <option value="community">Community</option>
                      <option value="concession">Concession</option>
                    </select>
                  </FormField>
                  <FormField label="Uncertainty at 95% CI (%) — Verra requires ≤ 30">
                    <input type="number" min="0" max="100" step="0.5" value={uncertaintyPct}
                      onChange={(e) => setUncertaintyPct(parseFloat(e.target.value) || 0)}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                      data-testid="hub-submit-uncertainty" />
                  </FormField>
                  <FormField label="Sampling plot count">
                    <input type="number" min="0" value={samplingPlotCount}
                      onChange={(e) => setSamplingPlotCount(parseInt(e.target.value, 10) || 0)}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                      data-testid="hub-submit-plot-count" />
                  </FormField>
                  <FormField label="Sampling plot size (m²)">
                    <input type="number" min="0" step="1" value={samplingPlotSize}
                      onChange={(e) => setSamplingPlotSize(parseFloat(e.target.value) || 0)}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                      data-testid="hub-submit-plot-size" />
                  </FormField>
                </div>
                <div className="mt-3">
                  <p className="text-[11px] uppercase tracking-wider text-stone-500 mb-2">SDG contributions (Gold Standard co-benefits)</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="hub-submit-sdg-list">
                    {SDG_OPTIONS.map((sdg) => {
                      const active = sdgContributions.includes(sdg.code);
                      return (
                        <button type="button" key={sdg.code}
                          onClick={() => setSdgContributions(active
                            ? sdgContributions.filter((c) => c !== sdg.code)
                            : [...sdgContributions, sdg.code])}
                          className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                            active
                              ? "bg-emerald-600 border-emerald-600 text-white"
                              : "bg-white border-stone-200 text-stone-600 hover:border-emerald-300"
                          }`}
                          data-testid={`hub-submit-sdg-${sdg.code}`}>
                          {sdg.code} · {sdg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </details>
            </>
          )}

          {step === 3 && (
            <>
              <SectionTitle title="3. Required Documents" hint="A local council letter authorising the project, plus a geotagged photo from the site." />
              <FormField label="Local Council Letter (PDF, JPG, PNG · ≤5MB)">
                <label className="flex items-center gap-3 bg-stone-50 border border-dashed border-stone-300 rounded-lg px-4 py-4 cursor-pointer hover:bg-stone-100"
                  data-testid="hub-submit-council-label">
                  <Upload size={18} className="text-stone-500" />
                  <span className="text-sm text-stone-600 flex-1 truncate">{councilFile ? councilFile.name : "Click to upload council letter"}</span>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
                    onChange={(e) => setCouncilFile(e.target.files?.[0] || null)}
                    className="hidden" data-testid="hub-submit-council-input" />
                </label>
              </FormField>
              <FormField label="Geotagged Site Photo (JPG, PNG · ≤5MB)">
                <label className="flex items-center gap-3 bg-stone-50 border border-dashed border-stone-300 rounded-lg px-4 py-4 cursor-pointer hover:bg-stone-100"
                  data-testid="hub-submit-photo-label">
                  <Camera size={18} className="text-stone-500" />
                  <span className="text-sm text-stone-600 flex-1 truncate">{photoFile ? photoFile.name : "Take photo or upload from device"}</span>
                  <input ref={photoRef} type="file" accept="image/*" capture="environment"
                    onChange={onPhotoChange}
                    className="hidden" data-testid="hub-submit-photo-input" />
                </label>
                {photoGeotag && (
                  <p className="text-xs text-emerald-700 mt-1.5 flex items-center gap-1" data-testid="hub-submit-photo-geotag">
                    <MapPin size={12} /> Geotag captured: {photoGeotag.lat.toFixed(5)}, {photoGeotag.lon.toFixed(5)}
                  </p>
                )}
              </FormField>
            </>
          )}

          {step === 4 && (
            <>
              <SectionTitle title="4. Review & Submit" hint="Once submitted, your project enters the admin approval queue." />
              <div className="bg-stone-50 rounded-2xl p-5 space-y-2 text-sm">
                <SumRow k="Location" v={`${lat?.toFixed(4)}, ${lon?.toFixed(4)} (${locationName || "no name"})`} />
                <SumRow k="Type" v={`${projectType} / ${speciesType}`} />
                <SumRow k="Area" v={`${areaHa} ha`} />
                <SumRow k="Duration" v={`${duration} years`} />
                <SumRow k="Price" v={`$${pricePerTon} / tCO₂e`} />
                <SumRow k="Council letter" v={councilFile?.name || "—"} />
                <SumRow k="Photo" v={photoFile?.name || "—"} />
                <SumRow k="Photo geotag" v={photoGeotag ? `${photoGeotag.lat.toFixed(4)}, ${photoGeotag.lon.toFixed(4)}` : "—"} />
              </div>
            </>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="hub-submit-error">
              {error}
            </p>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-stone-200 px-6 py-4 flex items-center justify-between gap-2">
          <button
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="text-sm text-stone-500 hover:text-stone-900 disabled:opacity-30"
            data-testid="hub-submit-back"
          >
            Back
          </button>
          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              className="bg-stone-900 text-white text-sm font-medium px-5 py-2 rounded-full hover:bg-stone-800"
              data-testid="hub-submit-next"
            >
              Continue
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-5 py-2 rounded-full disabled:bg-stone-400"
              data-testid="hub-submit-confirm"
            >
              {submitting ? "Submitting..." : "Submit for Approval"}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function SectionTitle({ title, hint }) {
  return (
    <div>
      <h3 className="font-heading text-base font-semibold text-stone-900">{title}</h3>
      {hint && <p className="text-xs text-stone-500 mt-1">{hint}</p>}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-stone-400 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function SumRow({ k, v }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-[11px] uppercase tracking-wider text-stone-400 w-32 flex-shrink-0 pt-0.5">{k}</span>
      <span className="text-sm text-stone-900 flex-1 break-all">{v}</span>
    </div>
  );
}



// ─── Flutterwave Buy Button ─────────────────────────────────

function makeBuyTxRef() {
  const rand = (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36)
  ).replace(/-/g, "");
  return `hub-${rand}`;
}

function FlutterwaveBuyButton({ project, onSuccess }) {
  const { user } = useAuth();
  const { config } = usePaymentConfig();
  const { convert, loaded: fxLoaded } = useFxRates();
  const [stage, setStage] = useState("idle");
  const [phone, setPhone] = useState("");
  const [buyerType, setBuyerType] = useState("individual");
  const [companyName, setCompanyName] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [showForm, setShowForm] = useState(false);
  const txRef = useRef(makeBuyTxRef()).current;

  const usdPrice = Number(project.total_price || 0);
  const chargedAmount = useMemo(() => {
    if (currency === "USD" || !fxLoaded) return usdPrice;
    return convert(usdPrice, "USD", currency);
  }, [usdPrice, currency, fxLoaded, convert]);

  const flwConfig = useMemo(() => ({
    public_key: config?.public_key || "",
    tx_ref: txRef,
    amount: chargedAmount,
    currency: currency,
    payment_options: getPaymentOptionsForCurrency(config, currency) || "card",
    customer: {
      email: user?.email || "buyer@conexusfoundation.io",
      name: buyerType === "company" && companyName ? companyName : (user?.name || "Buyer"),
      phone_number: phone || "",
    },
    customizations: {
      title: "Carbon Hub Purchase",
      description: project.location_name || project.project_type,
      logo: "",
    },
  }), [config, project, phone, user, buyerType, companyName, currency, chargedAmount, txRef]);

  const flw = useFlutterwave(flwConfig);

  const start = () => {
    if (!user) return alert("Sign in to purchase.");
    if (!phone.trim() || (buyerType === "company" && !companyName.trim())) {
      setShowForm(true);
      return;
    }
    if (!config?.public_key) return alert("Payment provider not configured.");
    if (!getPaymentOptionsForCurrency(config, currency)) {
      return alert(`No payment method is enabled for ${currency}. Choose a different currency.`);
    }
    setStage("initing");
    axios.post(`${API}/payments/hub/init/${project.id}`,
      { tx_ref: txRef, phone, buyer_type: buyerType, company_name: companyName, currency },
      { withCredentials: true }
    ).then(() => {
      setStage("paying");
      flw({
        callback: async (payment) => {
          closePaymentModal();
          try {
            const v = await axios.post(`${API}/payments/hub/verify`, {
              tx_ref: txRef, transaction_id: payment.transaction_id,
            }, { withCredentials: true });
            if (v.data.status === "successful") {
              const certId = v.data.certificate_id;
              const certSerial = v.data.certificate_serial;
              const downloadUrl = certId ? `${process.env.REACT_APP_BACKEND_URL}/api/certificates/${certId}/download` : "";
              if (certId) {
                window.open(downloadUrl, "_blank", "noopener");
                alert(`Purchase successful! Your retirement certificate (Serial: ${certSerial}) opened in a new tab and was also emailed to you.`);
              } else {
                alert("Purchase successful! The project is now yours.");
              }
              onSuccess();
            } else {
              alert("Payment did not complete. No charge was made.");
            }
          } catch (err) {
            alert(err.response?.data?.detail || "Verification failed.");
          }
          setStage("idle");
        },
        onClose: () => setStage("idle"),
      });
    }).catch((e) => {
      alert(e.response?.data?.detail || "Could not start payment");
      setStage("idle");
    });
  };

  return (
    <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
      {showForm && (
        <div className="w-full sm:w-72 bg-stone-50 border border-stone-200 rounded-xl p-3 space-y-2" data-testid="hub-buyer-form">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Buyer type</p>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                type="button"
                onClick={() => setBuyerType("individual")}
                data-testid="hub-buyer-type-individual"
                className={`text-[11px] py-1.5 rounded-lg border ${buyerType === "individual" ? "bg-emerald-700 text-white border-emerald-800" : "border-stone-300 text-stone-600 hover:border-emerald-500"}`}
              >Individual</button>
              <button
                type="button"
                onClick={() => setBuyerType("company")}
                data-testid="hub-buyer-type-company"
                className={`text-[11px] py-1.5 rounded-lg border ${buyerType === "company" ? "bg-emerald-700 text-white border-emerald-800" : "border-stone-300 text-stone-600 hover:border-emerald-500"}`}
              >Company</button>
            </div>
          </div>
          {buyerType === "company" && (
            <input
              type="text" placeholder="Company name *"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full px-3 py-1.5 border border-stone-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
              data-testid="hub-buy-company-name"
            />
          )}
          <input type="tel" placeholder="Phone number *" value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-3 py-1.5 border border-stone-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
            data-testid="hub-buy-phone" />

          {/* Payment currency selector with live FX equivalent */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Pay in</p>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              data-testid="hub-buy-currency"
              className="w-full px-3 py-1.5 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:border-emerald-500"
            >
              {(config?.currencies || ["USD"]).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <p className="text-[10px] text-stone-500 mt-1" data-testid="hub-buy-fx-line">
              Price: <strong className="text-stone-700">US${usdPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              {currency !== "USD" && fxLoaded && (
                <> · You&apos;ll pay <strong className="text-emerald-700">{currency} {chargedAmount.toLocaleString()}</strong> at live rate</>
              )}
            </p>
          </div>

          <p className="text-[10px] text-stone-500">
            The certificate will be issued in {buyerType === "company" ? "the company's name" : "your name"}.
          </p>
        </div>
      )}
      <button
        onClick={start}
        disabled={stage !== "idle" || !config?.public_key}
        className="bg-emerald-700 hover:bg-emerald-800 disabled:bg-stone-400 text-white text-sm font-medium px-5 py-2 rounded-full flex items-center gap-1.5"
        data-testid="hub-buy-button"
      >
        <CreditCard size={14} weight="bold" /> {stage === "idle" ? "Buy Credits" : "Processing..."}
      </button>
    </div>
  );
}

